import React, { useState, useEffect } from 'react';
import { MessageSquare, AlertCircle, CheckCircle, Clock, Send } from 'lucide-react';

const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [response, setResponse] = useState('');

  useEffect(() => {
    fetchTickets();
  }, [statusFilter]);

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const url = statusFilter
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/support-tickets?status=${statusFilter}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/support-tickets`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setTickets(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateTicket = async (ticketId, updateData) => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/support-tickets/${ticketId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      if (!res.ok) throw new Error('Failed');
      alert('Тикет обновлен');
      fetchTickets();
      setSelectedTicket(null);
      setResponse('');
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'in_progress': return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'resolved': return <CheckCircle className="w-5 h-5 text-green-500" />;
      default: return <MessageSquare className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: 'bg-red-500/20 text-red-400',
      in_progress: 'bg-yellow-500/20 text-yellow-400',
      resolved: 'bg-green-500/20 text-green-400',
      closed: 'bg-gray-500/20 text-gray-400'
    };
    const labels = {
      open: 'Открыт',
      in_progress: 'В работе',
      resolved: 'Решен',
      closed: 'Закрыт'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status] || badges.open}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div>
      <div className="mb-6">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-full md:w-64 px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          <option value="">Все тикеты</option>
          <option value="open">Открытые</option>
          <option value="in_progress">В работе</option>
          <option value="resolved">Решенные</option>
          <option value="closed">Закрытые</option>
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {loading ? (
          <div className="col-span-2 text-center py-8 text-gray-400">Загрузка...</div>
        ) : tickets.length === 0 ? (
          <div className="col-span-2 text-center py-8 text-gray-400">Тикеты не найдены</div>
        ) : (
          tickets.map((ticket) => (
            <div
              key={ticket.id}
              onClick={() => setSelectedTicket(ticket)}
              className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-gray-600 cursor-pointer transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {getStatusIcon(ticket.status)}
                  <div>
                    <h3 className="text-lg font-bold text-white">{ticket.subject}</h3>
                    <p className="text-sm text-gray-400">{ticket.user_name} • {ticket.user_email}</p>
                  </div>
                </div>
                {getStatusBadge(ticket.status)}
              </div>
              <p className="text-gray-300 mb-3 line-clamp-2">{ticket.message}</p>
              <div className="text-xs text-gray-500">
                {new Date(ticket.created_at).toLocaleString('ru-RU')}
              </div>
            </div>
          ))
        )}
      </div>

      {selectedTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-900 border-b border-gray-700 p-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white">{selectedTicket.subject}</h2>
                <p className="text-sm text-gray-400">{selectedTicket.user_email}</p>
              </div>
              <button onClick={() => setSelectedTicket(null)} className="text-gray-400 hover:text-white">✕</button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-400 mb-2">Сообщение пользователя:</h3>
                <div className="bg-gray-800/50 rounded-lg p-4 text-white">{selectedTicket.message}</div>
              </div>
              {selectedTicket.admin_response && (
                <div>
                  <h3 className="text-sm font-medium text-gray-400 mb-2">Ваш ответ:</h3>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-white">
                    {selectedTicket.admin_response}
                  </div>
                </div>
              )}
              <div>
                <h3 className="text-sm font-medium text-gray-400 mb-2">Ответ пользователю:</h3>
                <textarea
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  placeholder="Напишите ответ..."
                  className="w-full h-32 px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => updateTicket(selectedTicket.id, { status: 'in_progress', admin_response: response })}
                  className="flex-1 px-4 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Clock className="w-5 h-5" />
                  В работу
                </button>
                <button
                  onClick={() => updateTicket(selectedTicket.id, { status: 'resolved', admin_response: response })}
                  className="flex-1 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  Решен
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTickets;